import { useEffect, useMemo, useRef } from 'react';
import { useQuery, QueryClient } from 'react-query';
import { useLocationGroupStore } from '@frontend/scope';
import { executeWetagQuery, wetagQueryKeys, getWetagConfig } from './api';
import { wetagDevtools } from './devtools-store';
import { normalizeToQuery } from './query-builder';
import type { WetagQueryOptions, WetagResult, WetagQueryResponse, WetagInput } from './types';

const captureCallStack = (): string | undefined => {
  if (!wetagDevtools.isEnabled() || import.meta.env.PROD) return undefined;
  const stack = new Error().stack;
  if (!stack) return undefined;
  const lines = stack.split('\n');
  const filtered = lines.filter((line) => !line.includes('wetags/src/'));
  return filtered.join('\n');
};

const DEFAULT_OPTIONS: Required<WetagQueryOptions> = {
  cacheTime: Infinity,
  staleTime: Infinity,
  defaultValue: false,
  enabled: true,
};

export function useWetag<TTag extends string = string>(
  namespaceId: string,
  input: WetagInput<TTag>,
  entityId: string | undefined,
  options: WetagQueryOptions = {}
): [boolean, boolean, WetagQueryResponse | undefined] {
  const result = useWetagQuery(namespaceId, input, entityId, options);
  return [result.isEnabled, result.isLoading, result.response];
}

export function useWetagQuery<TTag extends string = string>(
  namespaceId: string,
  input: WetagInput<TTag>,
  entityId: string | undefined,
  options: WetagQueryOptions = {}
): WetagResult {
  const config = getWetagConfig();
  const hasLocationsSelected = useLocationGroupStore((state) => state.selectedLocationIds.length > 0);

  const callStackRef = useRef<string | undefined>(captureCallStack());

  const query = useMemo(() => normalizeToQuery(input), [input]);

  const mergedOptions = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      ...config.defaultOptions,
      ...options,
    }),
    [config.defaultOptions, options]
  );

  const tagId = typeof input === 'string' ? input : 'complex-query';

  const { cacheTime, staleTime, defaultValue, enabled } = mergedOptions;
  const isQueryEnabled = enabled && !!entityId && hasLocationsSelected;

  const overrideValue = useMemo(() => {
    if (wetagDevtools.isEnabled() && entityId && wetagDevtools.hasOverride(namespaceId, tagId, entityId)) {
      return wetagDevtools.getOverride(namespaceId, tagId, entityId);
    }
    return undefined;
  }, [namespaceId, tagId, entityId]);

  const hasOverride = overrideValue !== undefined;

  const reactQuery = useQuery(
    wetagQueryKeys.query(namespaceId, entityId || '', query),
    () => executeWetagQuery({ namespaceId, query, entityId: entityId }),
    {
      enabled: isQueryEnabled && !hasOverride,
      cacheTime,
      staleTime,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  );

  const isEnabled = hasOverride ? overrideValue : reactQuery.data?.result ?? defaultValue;

  useEffect(() => {
    if (!wetagDevtools.isEnabled() || !entityId) return;
    wetagDevtools.log('call', namespaceId, tagId, entityId, isEnabled, { stack: callStackRef.current });
    wetagDevtools.observe(namespaceId, tagId, entityId, isEnabled);
  }, [entityId, namespaceId, tagId, isEnabled]);

  if (hasOverride) {
    return {
      isEnabled: overrideValue,
      isLoading: false,
      isError: false,
      error: null,
      isSuccess: true,
      refetch: reactQuery.refetch,
      response: reactQuery.data,
    };
  }

  return {
    isEnabled: reactQuery.isError ? defaultValue : isEnabled,
    isLoading: reactQuery.isLoading,
    isError: reactQuery.isError,
    error: reactQuery.error as Error | null,
    isSuccess: reactQuery.isSuccess,
    refetch: reactQuery.refetch,
    response: reactQuery.data,
  };
}

export async function prefetchWetag<TTag extends string = string>(
  queryClient: QueryClient,
  namespaceId: string,
  input: WetagInput<TTag>,
  entityId: string,
  options: WetagQueryOptions = {}
): Promise<void> {
  const callStack = captureCallStack();
  const config = getWetagConfig();
  const query = normalizeToQuery(input);

  const mergedOptions = {
    ...DEFAULT_OPTIONS,
    ...config.defaultOptions,
    ...options,
  };

  const { cacheTime, staleTime } = mergedOptions;
  const tagId = typeof input === 'string' ? input : 'complex-query';

  wetagDevtools.log('prefetch', namespaceId, tagId, entityId, undefined, { stack: callStack });

  await queryClient.prefetchQuery(
    wetagQueryKeys.query(namespaceId, entityId, query),
    () => executeWetagQuery({ namespaceId, query, entityId }),
    { cacheTime, staleTime }
  );
}

export function getWetagFromCache<TTag extends string = string>(
  queryClient: QueryClient,
  namespaceId: string,
  input: WetagInput<TTag>,
  entityId: string
): boolean | undefined {
  const query = normalizeToQuery(input);
  const data = queryClient.getQueryData<WetagQueryResponse>(wetagQueryKeys.query(namespaceId, entityId, query));
  return data?.result;
}

export function invalidateWetag<TTag extends string = string>(
  queryClient: QueryClient,
  namespaceId: string,
  input: WetagInput<TTag>,
  entityId: string
): Promise<void> {
  const callStack = captureCallStack();
  const query = normalizeToQuery(input);
  const tagId = typeof input === 'string' ? input : 'complex-query';
  wetagDevtools.log('invalidate', namespaceId, tagId, entityId, undefined, { stack: callStack });
  return queryClient.invalidateQueries(wetagQueryKeys.query(namespaceId, entityId, query));
}

export function invalidateEntityWetags(queryClient: QueryClient, namespaceId: string, entityId: string): Promise<void> {
  wetagDevtools.log('invalidate', namespaceId, '*', entityId, undefined, { stack: captureCallStack() });
  return queryClient.invalidateQueries(wetagQueryKeys.entity(namespaceId, entityId));
}

export function invalidateNamespaceWetags(queryClient: QueryClient, namespaceId: string): Promise<void> {
  wetagDevtools.log('invalidate', namespaceId, '*', '*', undefined, { stack: captureCallStack() });
  return queryClient.invalidateQueries(wetagQueryKeys.namespace(namespaceId));
}

export function invalidateAllWetags(queryClient: QueryClient): Promise<void> {
  wetagDevtools.log('invalidate', '*', '*', '*', undefined, { stack: captureCallStack() });
  return queryClient.invalidateQueries(wetagQueryKeys.all);
}
